import React from "react";

import {
  ChevronDown,
  ChevronUp,
  DataError,
  DotMark,
  GroupAccess,
  ListChecked,
  ReplyAll,
  SearchLocate,
} from "@carbon/icons-react";
import cx from "classnames";
import { NavLink, Outlet, useSearchParams } from "react-router-dom";

import SubNav from "@/components/layout/SubNav/SubNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAppSelector } from "@/redux/store";

import CreateGroupModal from "../components/CreateGroupModal/CreateGroupModal";
import { GroupRoleEnum } from "../constants/group.constant";
import { GroupPathsEnum } from "../constants/group.paths";

const navLinks = [
  {
    url: GroupPathsEnum.GROUP_DISCOVER,
    end: true,
    icon: <SearchLocate size={18} />,
    label: "Khám phá nhóm",
    badge: null,
  },
  {
    url: GroupPathsEnum.GROUP_INVITE,
    end: true,
    icon: <ListChecked size={18} />,
    label: "Lời mời tham gia",
    badge: "new",
  },
  {
    url: GroupPathsEnum.GROUP_REQUEST,
    end: true,
    icon: <ReplyAll size={18} />,
    label: "Yêu cầu đã gửi",
    badge: null,
  },
  {
    url: GroupPathsEnum.USER_GROUP_DELETED,
    end: true,
    icon: <DataError size={18} />,
    label: "Nhóm đã xóa",
    badge: null,
  },
];

const GroupLayout = () => {
  const flagList = useAppSelector((state) => state.group.flagList);
  const [isOpenCollage, setIsOpenCollage] = React.useState(true);
  const [searchParams] = useSearchParams();

  return (
    <div className="min-h-screen bg-secondary-25">
      <SubNav>
        <div className="w-full h-full flex flex-col">
          {/* Header Section */}
          <div className="p-6  bg-white">
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-2xl font-bold text-secondary-900">Nhóm</h1>
            </div>
            <p className="text-sm text-secondary-600">
              Quản lý và tham gia các nhóm của bạn
            </p>
          </div>

          {/* Navigation Section */}
          <div className="flex-1 p-4 bg-white">
            <div className="space-y-2">
              {/* My Groups Section */}
              <Collapsible
                open={isOpenCollage}
                onOpenChange={setIsOpenCollage}
                className="space-y-1"
              >
                <NavLink
                  to={GroupPathsEnum.GROUP}
                  end
                >
                  {({ isActive }) => (
                    <div
                      className={cx(
                        "group flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200",
                        isActive && !searchParams.get("roleFilter")
                          ? "bg-primary-50 text-primary-700 shadow-sm"
                          : "text-secondary-700 hover:bg-secondary-50"
                      )}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={cx(
                            "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                            isActive && !searchParams.get("roleFilter")
                              ? "bg-primary-100"
                              : "bg-secondary-100 group-hover:bg-secondary-200"
                          )}
                        >
                          <GroupAccess
                            size={18}
                            className={cx(
                              isActive && !searchParams.get("roleFilter")
                                ? "text-primary-600"
                                : "text-secondary-600"
                            )}
                          />
                        </div>
                        <span className="font-medium">Nhóm của tôi</span>
                      </div>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-6 h-6 p-0 hover:bg-transparent"
                        >
                          {isOpenCollage ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  )}
                </NavLink>

                <CollapsibleContent className="space-y-1">
                  <NavLink
                    className={({ isActive }) =>
                      cx(
                        "flex items-center px-3 py-2.5 ml-6 rounded-lg transition-all duration-200",
                        isActive && searchParams.get("roleFilter")
                          ? "bg-orange-50 text-orange-700"
                          : "text-secondary-600 hover:bg-secondary-50"
                      )
                    }
                    end
                    to={`${GroupPathsEnum.GROUP}?roleFilter=${GroupRoleEnum.MANAGER}`}
                    state={{ from: "occupation" }}
                  >
                    <DotMark
                      size={6}
                      className="mr-3 text-orange-500"
                    />
                    <span className="text-sm font-medium">
                      Nhóm bạn quản lý
                    </span>
                  </NavLink>
                </CollapsibleContent>
              </Collapsible>

              {/* Divider */}
              <div className="h-px bg-secondary-200 my-4"></div>

              {/* Other Navigation Items */}
              <div className="space-y-1">
                {navLinks.map((navLink, index) => (
                  <NavLink
                    key={index}
                    to={navLink.url}
                    end={navLink.end}
                    className={({ isActive }) =>
                      cx(
                        "group flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200",
                        isActive
                          ? "bg-primary-50 text-primary-700 shadow-sm"
                          : "text-secondary-700 hover:bg-secondary-50"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div className="flex items-center space-x-3">
                          <div
                            className={cx(
                              "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                              isActive
                                ? "bg-primary-100"
                                : "bg-secondary-100 group-hover:bg-secondary-200"
                            )}
                          >
                            <div
                              className={cx(
                                isActive
                                  ? "text-primary-600"
                                  : "text-secondary-600"
                              )}
                            >
                              {navLink.icon}
                            </div>
                          </div>
                          <span className="font-medium">{navLink.label}</span>
                        </div>
                        {navLink.badge && (
                          <Badge
                            variant="secondary"
                            className="bg-red-100 text-red-700 text-xs px-2 py-1"
                          >
                            {navLink.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          {/* Create Group Button */}
          {!flagList && (
            <div className="p-4 border-t border-secondary-200 bg-white">
              <CreateGroupModal
                isFullWidth
                haveSubGroup={true}
              />
            </div>
          )}
        </div>
      </SubNav>

      {/* Main Content */}
      <div className="content pl-[280px] pt-[70px] min-h-screen bg-secondary-25">
        <Outlet />
      </div>
    </div>
  );
};

export default GroupLayout;
